import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import type { ContactFormData, ContactFormErrors, ContactInfo } from '../types';
import { postContact } from '../config/api';
import { Icon } from './Icon';

const ContactSection = styled.section`
  padding: 5rem 0;
  background-color: var(--surface-section);
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 75rem;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 1rem;
  }
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.75rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 37.5rem;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-top: 3rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactForm = styled(motion.form)`
  background: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.xl};
  padding: 3rem 2.5rem;
  border-radius: 1.25rem;
  border: 0.0625rem solid ${props => props.theme.colors.lightGray};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 2rem 1.5rem;
  }
`;

const FormTitle = styled.h3`
  font-size: 1.75rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.text};
  text-align: center;
  font-weight: 600;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 1rem;
  border: 0.125rem solid ${props =>
    props.hasError ? 'var(--color-danger-strong)' : props.theme.colors.lightGray};
  border-radius: 0.625rem;
  background: var(--surface-sunken);
  color: ${props => props.theme.colors.text};
  font-size: 1rem;
  transition: all 0.3s ease;

  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.white};
    box-shadow: 0 0 0 0.1875rem rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 1rem;
  border: 0.125rem solid ${props =>
    props.hasError ? 'var(--color-danger-strong)' : props.theme.colors.lightGray};
  border-radius: 0.625rem;
  background: var(--surface-sunken);
  color: ${props => props.theme.colors.text};
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.white};
    box-shadow: 0 0 0 0.1875rem rgba(59, 130, 246, 0.1);
  }

  option {
    background-color: ${props => props.theme.colors.white};
    color: ${props => props.theme.colors.text};
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  padding: 1rem;
  border: 0.125rem solid ${props =>
    props.hasError ? 'var(--color-danger-strong)' : props.theme.colors.lightGray};
  border-radius: 0.625rem;
  background: var(--surface-sunken);
  color: ${props => props.theme.colors.text};
  font-size: 1rem;
  min-height: 7.5rem;
  resize: vertical;
  transition: all 0.3s ease;

  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.white};
    box-shadow: 0 0 0 0.1875rem rgba(59, 130, 246, 0.1);
  }
`;

const ErrorMessage = styled.span`
  display: block;
  color: var(--color-danger-strong);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const Checkbox = styled.input`
  margin-top: 0.25rem;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  line-height: 1.4;
  color: ${props => props.theme.colors.textLight};
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  background: ${props => props.theme.gradients.primary};
  color: ${props => props.theme.colors.white};
  padding: 1.25rem 2rem;
  border: none;
  border-radius: 0.625rem;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-0.125rem);
    box-shadow: ${props => props.theme.shadows.xl};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContactInfoSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const InfoCard = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.lg};
  padding: 2.5rem 2rem;
  border-radius: 1.25rem;
  border: 0.0625rem solid ${props => props.theme.colors.lightGray};
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-0.3125rem);
    box-shadow: ${props => props.theme.shadows.xl};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 2rem 1.5rem;
  }
`;

const InfoIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
`;

const InfoTitle = styled.h4`
  font-size: 1.5rem;
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ActionButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  color: ${props => props.theme.colors.primary};
  border: 0.125rem solid ${props => props.theme.colors.primary};
  padding: 0.75rem 1.5rem;
  border-radius: 1.5625rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.gradients.primary};
    color: ${props => props.theme.colors.white};
    transform: translateY(-0.125rem);
  }
`;

const MapContainer = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.lg};
  padding: 2rem;
  border-radius: 1.25rem;
  border: 0.0625rem solid ${props => props.theme.colors.lightGray};
  text-align: center;
  height: 18.75rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-0.3125rem);
    box-shadow: ${props => props.theme.shadows.xl};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1.5rem;
    height: 15.625rem;
  }
`;

const MapPlaceholder = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.7;
`;

const MapText = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
  font-weight: 600;
`;

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    acceptTerms: false,
  });

  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactInfo: ContactInfo = {
    phone: '+52 951 321 4264',
    whatsapp: '+52 951 321 4264',
    email: 'contacto@vidrerialacanadita.com',
    address: 'Col. La cañadita Santa María Atzompa',
    hours: 'Lun - Vie: 8:00 AM - 6:00 PM, Sáb: 8:00 AM - 2:00 PM',
    socialMedia: {
      facebook: 'https://facebook.com/vidrerialacanadita',
    },
  };

  const validateForm = (): boolean => {
    const newErrors: ContactFormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    }

    if (!formData.service) {
      newErrors.service = 'Selecciona un servicio';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'El mensaje es requerido';
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Debes aceptar los términos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const honeypot =
      (e.currentTarget.elements.namedItem('company_website') as HTMLInputElement | null)?.value ?? '';

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Send contact form data to API
      const result = await postContact({
          ...formData,
          company_website: honeypot
      });

      if (result.success) {
        // Send WhatsApp message with form data (as backup/alternative)
        const message = `
¡Hola! He enviado una solicitud de presupuesto con los siguientes datos:

Nombre: ${formData.name}
Email: ${formData.email}
Teléfono: ${formData.phone}
Servicio: ${formData.service}
Mensaje: ${formData.message}

¡Espero su respuesta!
        `.trim();

        window.open(`https://wa.me/529513214264?text=${encodeURIComponent(message)}`, '_blank');

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
          acceptTerms: false,
        });

        alert('¡Formulario enviado exitosamente! Hemos enviado tu información por email y te redirigimos a WhatsApp para continuar la conversación.');
      } else {
        throw new Error(result.message || 'Error al enviar el formulario');
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      alert('Error al enviar el formulario. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof ContactFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/529513214264?text=Hola, me interesa obtener más información sobre sus servicios.`, '_blank');
  };

  const handlePhoneClick = () => {
    window.open(`tel:${contactInfo.phone}`, '_self');
  };

  const handleEmailClick = () => {
    window.open(`mailto:${contactInfo.email}?subject=Consulta sobre servicios`, '_self');
  };

  return (
    <ContactSection id="contacto">
      <Container>
        <SectionHeader
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <SectionTitle>Contáctanos</SectionTitle>
          <SectionSubtitle>
            ¿Listo para empezar tu proyecto? Envíanos un mensaje o llámanos. 
            Estamos aquí para convertir tu visión en realidad.
          </SectionSubtitle>
        </SectionHeader>

        <ContactGrid>
          <ContactForm 
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <FormTitle>Solicita tu cotización</FormTitle>
            {/* Honeypot (SPEC-003 R9): hidden from people, filled by bots. */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-100vw', width: 1, height: 1, overflow: 'hidden' }}>
              <label>
                Website
                <input type="text" name="company_website" tabIndex={-1} autoComplete="off" defaultValue="" />
              </label>
            </div>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="name">Nombre Completo *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Tu nombre"
                  value={formData.name}
                  onChange={handleInputChange}
                  hasError={!!errors.name}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Teléfono *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="951 321 4264"
                  value={formData.phone}
                  onChange={handleInputChange}
                  hasError={!!errors.phone}
                />
                {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={handleInputChange}
                hasError={!!errors.email}
              />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="service">Servicio de Interés *</Label>
              <Select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleInputChange}
                hasError={!!errors.service}
              >
                <option value="">Selecciona un servicio</option>
                <option value="puertas">Puertas</option>
                <option value="ventanas">Ventanas</option>
                <option value="canceles">Cancelería de baños</option>
                <option value="barandales">Barandales</option>
                <option value="espejos">Espejos</option>
                <option value="vidrios">Vidrios de diferentes espesor</option>
                <option value="otro">Otro (especificar en mensaje)</option>
              </Select>
              {errors.service && <ErrorMessage>{errors.service}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Mensaje *</Label>
              <TextArea
                id="message"
                name="message"
                placeholder="Cuéntanos sobre tu proyecto: medidas aproximadas, ubicación, fecha deseada, etc."
                value={formData.message}
                onChange={handleInputChange}
                hasError={!!errors.message}
              />
              {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
            </FormGroup>

            <CheckboxContainer>
              <Checkbox
                id="acceptTerms"
                name="acceptTerms"
                type="checkbox"
                checked={formData.acceptTerms}
                onChange={handleInputChange}
              />
              <CheckboxLabel htmlFor="acceptTerms">
                Acepto los términos y condiciones. Autorizo el uso de mis datos 
                para fines comerciales relacionados con Vidrería la Cañadita.
              </CheckboxLabel>
            </CheckboxContainer>
            {errors.acceptTerms && <ErrorMessage>{errors.acceptTerms}</ErrorMessage>}

            <SubmitButton 
              type="submit" 
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? <><Icon name="LoaderCircle" size={18} /> Enviando...</> : <><Icon name="Mail" size={18} /> Enviar Mensaje</>}
            </SubmitButton>
          </ContactForm>

          <ContactInfoSection
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <InfoCard
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <InfoIcon><Icon name="Smartphone" size={28} /></InfoIcon>
              <InfoTitle>WhatsApp</InfoTitle>
              <InfoText>
                La forma más rápida de contactarnos. 
                ¡Respuesta inmediata garantizada!
              </InfoText>
              <ActionButton 
                onClick={handleWhatsAppClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Icon name="MessageCircle" size={18} /> Chatear Ahora
              </ActionButton>
            </InfoCard>

            <InfoCard
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <InfoIcon><Icon name="Phone" size={28} /></InfoIcon>
              <InfoTitle>Teléfono</InfoTitle>
              <InfoText>{contactInfo.phone}</InfoText>
              <InfoText>
                <strong>Horario:</strong><br />
                {contactInfo.hours}
              </InfoText>
              <ActionButton 
                onClick={handlePhoneClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Icon name="Phone" size={18} /> Llamar Ahora
              </ActionButton>
            </InfoCard>

            <InfoCard
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <InfoIcon><Icon name="Mail" size={28} /></InfoIcon>
              <InfoTitle>Email</InfoTitle>
              <InfoText>{contactInfo.email}</InfoText>
              <InfoText>
                Para consultas detalladas y envío de documentos
              </InfoText>
              <ActionButton 
                onClick={handleEmailClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Icon name="Mail" size={18} /> Enviar Email
              </ActionButton>
            </InfoCard>

            <MapContainer
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              onClick={() => window.open('https://maps.google.com/?q=La+Cañadita,+Oaxaca+de+Juárez,+Oaxaca', '_blank')}
            >
              <MapPlaceholder><Icon name="MapPin" size={40} /></MapPlaceholder>
              <MapText>Ubicados en {contactInfo.address}</MapText>
              <MapText>¡Visítanos en Oaxaca!</MapText>
            </MapContainer>
          </ContactInfoSection>
        </ContactGrid>
      </Container>
    </ContactSection>
  );
};
